import { useEffect, useState } from 'react';
import { User, CheckCircle2, AlertCircle, X, Linkedin, Github, Globe } from 'lucide-react';

import { useAuthStore } from '@/stores/authStore';
import { getMe, updateProfile } from '@/lib/api/auth';
import { cn } from '@/lib/cn';

const LINK_FIELDS = [
  { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/…' },
  { key: 'github', label: 'GitHub', icon: Github, placeholder: 'https://github.com/…' },
  { key: 'website', label: 'Website', icon: Globe, placeholder: 'https://…' },
] as const;


interface ProfileSnapshot {
  name: string;
  headline: string;
  bio: string;
  specializations: string[];
  links: Record<string, string>;
}

const EMPTY: ProfileSnapshot = { name: '', headline: '', bio: '', specializations: [], links: {} };

function sameProfile(a: ProfileSnapshot, b: ProfileSnapshot): boolean {
  return (
    a.name === b.name &&
    a.headline === b.headline &&
    a.bio === b.bio &&
    a.specializations.join('') === b.specializations.join('') &&
    JSON.stringify(a.links) === JSON.stringify(b.links)
  );
}

const inputCls =
  'h-9 w-full rounded-lg border border-border bg-white px-3 text-[13px] text-foreground placeholder:text-muted-foreground/40 transition-colors focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-50';

/**
 * ProfileSettingsForm is the shared profile editor used by the trainer, admin
 * and student settings pages. It owns its own data loading (getMe), saving
 * (PATCH /me) and avatar upload, and refreshes the auth store so the navbar /
 * sidebar update instantly after a change.
 */
export function ProfileSettingsForm() {
  const { user, setAuth } = useAuthStore();

  const [form, setForm] = useState<ProfileSnapshot>(EMPTY);
  const [initial, setInitial] = useState<ProfileSnapshot>(EMPTY);
  const [tagDraft, setTagDraft] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getMe()
      .then((me) => {
        if (!active) return;
        const snap: ProfileSnapshot = {
          name: me.username ?? '',
          headline: me.headline ?? '',
          bio: me.bio ?? '',
          specializations: me.specializations ?? [],
          links: me.links ?? {},
        };
        setForm(snap);
        setInitial(snap);
      })
      .catch(() => {
        if (!active || !user) return;
        const snap: ProfileSnapshot = { ...EMPTY, name: user.username ?? '' };
        setForm(snap);
        setInitial(snap);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) return null;

  const trimmedName = form.name.trim();
  const dirty = !sameProfile(
    { ...form, name: trimmedName, headline: form.headline.trim() },
    initial,
  );

  const update = (patch: Partial<ProfileSnapshot>) => setForm((f) => ({ ...f, ...patch }));

  // --- tag input ---
  const addTag = (raw: string) => {
    const tag = raw.trim();
    if (!tag) return;
    if (form.specializations.length >= 10) return;
    if (form.specializations.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      setTagDraft('');
      return;
    }
    update({ specializations: [...form.specializations, tag.slice(0, 30)] });
    setTagDraft('');
  };
  const removeTag = (tag: string) =>
    update({ specializations: form.specializations.filter((t) => t !== tag) });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (trimmedName.length < 3 || trimmedName.length > 50) {
      setError('Display name must be between 3 and 50 characters.');
      return;
    }
    if (form.headline.length > 120) {
      setError('Headline must be at most 120 characters.');
      return;
    }
    if (form.bio.length > 500) {
      setError('Bio must be at most 500 characters.');
      return;
    }

    setSaving(true);
    try {
      const res = await updateProfile({
        username: trimmedName,
        headline: form.headline.trim(),
        bio: form.bio,
        specializations: form.specializations,
        links: form.links,
      });
      setAuth(res.access_token);
      const snap: ProfileSnapshot = {
        name: res.user.username ?? trimmedName,
        headline: res.user.headline ?? form.headline.trim(),
        bio: res.user.bio ?? form.bio,
        specializations: res.user.specializations ?? form.specializations,
        links: res.user.links ?? form.links,
      };
      setForm(snap);
      setInitial(snap);
      setSuccess(true);
    } catch (err: any) {
      const msg = err?.response?.data?.error;
      if (msg === 'username_taken') setError('That display name is already taken. Try another.');
      else if (typeof msg === 'string' && msg) setError(msg.replace(/_/g, ' '));
      else setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || loading;

  return (
    <div className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
      <div className="flex items-center gap-2.5 border-b border-border px-5 py-3.5">
        <User className="h-4 w-4 text-muted-foreground/60" />
        <span
          className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/60"
          style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
        >
          Profile
        </span>
      </div>

      <form onSubmit={handleSubmit} className="px-5 py-5">
        {success && (
          <div className="mb-5 flex items-center gap-2.5 rounded-xl bg-emerald-50 px-4 py-3 text-[13px] text-emerald-700 border border-emerald-100">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-500" />
            Profile updated successfully.
          </div>
        )}
        {error && (
          <div className="mb-5 flex items-center gap-2.5 rounded-xl bg-destructive/8 px-4 py-3 text-[13px] text-destructive border border-destructive/15">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Name + Headline side by side */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="display-name" className="block text-[13px] font-medium text-foreground">
                Display name
              </label>
              <input
                id="display-name"
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
                disabled={busy}
                maxLength={50}
                placeholder="Your name"
                className={inputCls}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="headline" className="block text-[13px] font-medium text-foreground">
                Headline
              </label>
              <input
                id="headline"
                value={form.headline}
                onChange={(e) => update({ headline: e.target.value })}
                disabled={busy}
                maxLength={120}
                placeholder="e.g. Senior Full-Stack Instructor · 8 yrs"
                className={inputCls}
              />
            </div>
          </div>

          {/* Bio */}
          <div className="space-y-1.5">
            <label htmlFor="bio" className="block text-[13px] font-medium text-foreground">
              Bio
            </label>
            <textarea
              id="bio"
              value={form.bio}
              onChange={(e) => update({ bio: e.target.value })}
              disabled={busy}
              rows={3}
              maxLength={500}
              placeholder="A short introduction — your specialization and experience."
              className="w-full rounded-lg border border-border bg-white px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground/40 transition-colors focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
            <p className="text-right text-[11.5px] text-muted-foreground/60">{form.bio.length}/500</p>
          </div>

          {/* Specializations */}
          <div className="space-y-1.5">
            <label htmlFor="spec" className="block text-[13px] font-medium text-foreground">
              Specializations
            </label>
            {form.specializations.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {form.specializations.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-[12px] font-medium text-foreground"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      disabled={busy}
                      className="text-muted-foreground/60 hover:text-destructive"
                      aria-label={`Remove ${tag}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              id="spec"
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addTag(tagDraft);
                } else if (e.key === 'Backspace' && !tagDraft && form.specializations.length) {
                  removeTag(form.specializations[form.specializations.length - 1]);
                }
              }}
              onBlur={() => addTag(tagDraft)}
              disabled={busy || form.specializations.length >= 10}
              placeholder={form.specializations.length >= 10 ? 'Maximum 10 tags' : 'Type a skill and press Enter (e.g. React, DSA, AWS)'}
              className={inputCls}
            />
          </div>

          {/* Links */}
          <div className="space-y-1.5">
            <label className="block text-[13px] font-medium text-foreground">Links</label>
            <div className="space-y-2">
              {LINK_FIELDS.map(({ key, label, icon: Icon, placeholder }) => (
                <div key={key} className="flex items-center gap-2">
                  <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground/50" aria-label={label} />
                  <input
                    type="url"
                    value={form.links[key] ?? ''}
                    onChange={(e) => update({ links: { ...form.links, [key]: e.target.value } })}
                    disabled={busy}
                    placeholder={placeholder}
                    className={inputCls}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={busy || !dirty}
            className={cn(
              'rounded-lg px-5 py-2 text-[13px] font-medium transition-all',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'bg-foreground text-background hover:opacity-90',
            )}
          >
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </div>
      </form>
    </div>
  );
}

